import { useState, useEffect } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  ClipboardList, Plus, RefreshCw, Bell, CalendarCheck, Trash2, X,
  Clock, User, MapPin, Sparkles, ArrowRight, Phone, Mail, AlertTriangle
} from "lucide-react";

const priorityStyles = {
  high: "bg-[#FDE8E8] text-rose-600 border-[#F5C6C6]",
  normal: "bg-[#FAFAFA] text-slate-600 border-slate-200",
  low: "bg-[#D4EDDA] text-[#155724] border-[#C3E6CB]",
};
const statusStyles = {
  active: "bg-[#D4EDDA] text-[#155724]",
  notified: "bg-[#CCE5FF] text-[#004085]",
  booked: "bg-slate-900 text-white",
  expired: "bg-slate-50 text-slate-400",
};

export default function WaitlistPage() {
  const [entries, setEntries] = useState([]);
  const [gapMatches, setGapMatches] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({ client_id: "", service_ids: [], preferred_provider_id: "", location_id: "", preferred_times: "any", priority: "normal", notes: "" });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [wlRes, cliRes, svcRes, provRes, locRes] = await Promise.all([
        api.get("/waitlist"),
        api.get("/clients?limit=100"),
        api.get("/services"),
        api.get("/staff/all"),
        api.get("/locations"),
      ]);
      setEntries(wlRes.data || []);
      setClients(cliRes.data?.clients || cliRes.data || []);
      setServices(svcRes.data || []);
      setProviders((provRes.data || []).filter(u => ["stylist", "esthetician", "nail_tech", "owner"].includes(u.role)));
      setLocations(locRes.data || []);
    } catch { toast.error("Failed to load data"); }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const addEntry = async () => {
    if (!form.client_id) { toast.error("Select a client"); return; }
    try {
      await api.post("/waitlist", form);
      toast.success("Added to waitlist");
      setShowAdd(false);
      setForm({ client_id: "", service_ids: [], preferred_provider_id: "", location_id: "", preferred_times: "any", priority: "normal", notes: "" });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add"); }
  };

  const removeEntry = async (id) => {
    await api.delete(`/waitlist/${id}`);
    toast.success("Removed");
    fetchAll();
  };

  const notifyClient = async (id) => {
    try {
      const res = await api.post(`/waitlist/${id}/notify`);
      toast.success(res.data?.message || "Notified");
      fetchAll();
    } catch { toast.error("Failed to notify"); }
  };

  const checkGaps = async () => {
    try {
      const res = await api.post("/waitlist/check-gaps", {});
      setGapMatches(res.data);
      if (res.data?.matches?.length > 0) {
        toast.success(`Found ${res.data.matches.length} potential matches!`);
      } else {
        toast.info("No matching gaps found right now");
      }
    } catch { toast.error("Failed to check gaps"); }
  };

  const bookFromMatch = async (entryId, gap) => {
    try {
      await api.post(`/waitlist/${entryId}/book`, {
        provider_id: gap.provider_id,
        location_id: gap.location_id,
        start_time: gap.start_time,
        end_time: gap.end_time,
      });
      toast.success("Booked from waitlist!");
      setGapMatches(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Booking failed"); }
  };

  const fmtTime = (iso) => {
    try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true }); }
    catch { return ""; }
  };

  if (loading) return <div className="p-8 flex items-center justify-center min-h-[400px]"><RefreshCw className="w-5 h-5 animate-spin text-rose-400" /></div>;

  const activeCount = entries.filter(e => e.status === "active").length;
  const notifiedCount = entries.filter(e => e.status === "notified").length;

  return (
    <div data-testid="waitlist-page" className="p-6 lg:p-8 max-w-[1400px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-semibold text-slate-900 tracking-tight">Waitlist & Fill-the-Gap</h1>
          <p className="text-sm text-slate-400 mt-1">Auto-fill cancellations with waitlisted clients</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={checkGaps} className="border-slate-200 text-slate-600" data-testid="check-gaps-btn">
            <Sparkles className="w-4 h-4 mr-1.5" /> Find Gaps
          </Button>
          <Button size="sm" onClick={() => setShowAdd(true)} className="bg-slate-900 text-white" data-testid="add-waitlist-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add to Waitlist
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div data-testid="stat-active" className="bg-white border border-slate-200 rounded-2xl p-5">
          <ClipboardList className="w-5 h-5 text-emerald-600 mb-2" />
          <p className="font-['Outfit'] text-2xl font-semibold text-slate-900">{activeCount}</p>
          <p className="text-xs text-slate-400">Active Waitlist</p>
        </div>
        <div data-testid="stat-notified" className="bg-white border border-slate-200 rounded-2xl p-5">
          <Bell className="w-5 h-5 text-[#004085] mb-2" />
          <p className="font-['Outfit'] text-2xl font-semibold text-slate-900">{notifiedCount}</p>
          <p className="text-xs text-slate-400">Notified</p>
        </div>
        <div data-testid="stat-total" className="bg-white border border-slate-200 rounded-2xl p-5">
          <CalendarCheck className="w-5 h-5 text-[#8B5CF6] mb-2" />
          <p className="font-['Outfit'] text-2xl font-semibold text-slate-900">{entries.length}</p>
          <p className="text-xs text-slate-400">Total Entries</p>
        </div>
      </div>

      {/* Gap Matches */}
      {gapMatches && gapMatches.matches?.length > 0 && (
        <div data-testid="gap-matches" className="bg-gradient-to-br from-[#D4EDDA] to-[#C3E6CB] border border-[#A3D9A5] rounded-2xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-['Outfit'] font-semibold text-[#155724] flex items-center gap-2"><Sparkles className="w-4 h-4" /> {gapMatches.matches.length} Gap Matches Found</h3>
            <Button size="sm" variant="ghost" onClick={() => setGapMatches(null)} className="text-[#155724]"><X className="w-4 h-4" /></Button>
          </div>
          {gapMatches.matches.map((m) => {
            const matchKey = `${m.gap.appointment_id}-${m.waitlist_entry.entry_id}`;
            return (
            <div key={matchKey} data-testid={`match-${matchKey}`} className="bg-white rounded-lg p-3 flex items-center gap-4">
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900">{m.gap.service_names.join(", ")} with {m.gap.provider_name}</p>
                <p className="text-xs text-slate-400">{fmtTime(m.gap.start_time)}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400" />
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900">{m.waitlist_entry.client_name}</p>
                <p className="text-xs text-slate-400">{m.waitlist_entry.service_names.join(", ")}</p>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => notifyClient(m.waitlist_entry.entry_id)} className="text-xs border-[#004085] text-[#004085]">
                  <Bell className="w-3 h-3 mr-1" /> Notify
                </Button>
                <Button size="sm" onClick={() => bookFromMatch(m.waitlist_entry.entry_id, m.gap)} className="text-xs bg-emerald-600 text-white">
                  <CalendarCheck className="w-3 h-3 mr-1" /> Book
                </Button>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Add Modal */}
      {showAdd && (
        <div data-testid="add-waitlist-modal" className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-['Outfit'] font-semibold text-slate-900">Add to Waitlist</h3>
            <Button variant="ghost" size="sm" onClick={() => setShowAdd(false)}><X className="w-4 h-4" /></Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Client *</label>
              <Select value={form.client_id} onValueChange={v => setForm(p => ({ ...p, client_id: v }))}>
                <SelectTrigger className="border-slate-200" data-testid="wl-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Service</label>
              <Select value={form.service_ids[0] || ""} onValueChange={v => setForm(p => ({ ...p, service_ids: [v] }))}>
                <SelectTrigger className="border-slate-200" data-testid="wl-service-select"><SelectValue placeholder="Any service" /></SelectTrigger>
                <SelectContent>{services.map(s => <SelectItem key={s.service_id} value={s.service_id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Preferred Provider</label>
              <Select value={form.preferred_provider_id} onValueChange={v => setForm(p => ({ ...p, preferred_provider_id: v }))}>
                <SelectTrigger className="border-slate-200"><SelectValue placeholder="Any provider" /></SelectTrigger>
                <SelectContent>{providers.map(p => <SelectItem key={p.user_id} value={p.user_id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Location</label>
              <Select value={form.location_id} onValueChange={v => setForm(p => ({ ...p, location_id: v }))}>
                <SelectTrigger className="border-slate-200"><SelectValue placeholder="Any location" /></SelectTrigger>
                <SelectContent>{locations.map(l => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Time Preference</label>
              <Select value={form.preferred_times} onValueChange={v => setForm(p => ({ ...p, preferred_times: v }))}>
                <SelectTrigger className="border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any Time</SelectItem>
                  <SelectItem value="morning">Morning (before 12pm)</SelectItem>
                  <SelectItem value="afternoon">Afternoon (12-5pm)</SelectItem>
                  <SelectItem value="evening">Evening (after 5pm)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Priority</label>
              <Select value={form.priority} onValueChange={v => setForm(p => ({ ...p, priority: v }))}>
                <SelectTrigger className="border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Notes</label>
            <Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional notes" className="border-slate-200" />
          </div>
          <Button onClick={addEntry} className="bg-slate-900 text-white" data-testid="wl-submit-btn">Add to Waitlist</Button>
        </div>
      )}

      {/* Entries Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200 bg-[#FAFAF9]">
              <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase">Client</th>
              <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase">Service</th>
              <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase">Provider</th>
              <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase">Time Pref</th>
              <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase">Priority</th>
              <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase">Status</th>
              <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12 text-slate-400">
                <ClipboardList className="w-8 h-8 mx-auto mb-2 text-[#D1D1D1]" />
                <p>No waitlist entries</p>
              </td></tr>
            ) : entries.map(e => (
              <tr key={e.entry_id} data-testid={`wl-row-${e.entry_id}`} className="border-b border-slate-100 hover:bg-[#FAFAF9]">
                <td className="py-3 px-4">
                  <p className="text-sm font-medium text-slate-900">{e.client_name}</p>
                  <p className="text-xs text-slate-400">{e.client_phone}</p>
                </td>
                <td className="py-3 px-4">
                  <div className="flex flex-wrap gap-1">{(e.service_names || []).map((s, i) => <span key={`${s}-${i}`} className="text-xs bg-[#FAFAFA] px-2 py-0.5 rounded border border-slate-200 text-slate-600">{s}</span>)}</div>
                </td>
                <td className="py-3 px-4 text-sm text-slate-600">{e.preferred_provider_name || "Any"}</td>
                <td className="py-3 px-4 text-center text-xs text-slate-600 capitalize">{e.preferred_times}</td>
                <td className="py-3 px-4 text-center">
                  <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${priorityStyles[e.priority] || priorityStyles.normal}`}>{e.priority}</span>
                </td>
                <td className="py-3 px-4 text-center">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusStyles[e.status] || statusStyles.active}`}>{e.status}</span>
                </td>
                <td className="py-3 px-4 text-center">
                  <div className="flex items-center justify-center gap-1">
                    {e.status === "active" && (
                      <Button variant="ghost" size="sm" onClick={() => notifyClient(e.entry_id)} className="text-[#004085]" data-testid={`notify-${e.entry_id}`}>
                        <Bell className="w-4 h-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => removeEntry(e.entry_id)} className="text-rose-600" data-testid={`remove-${e.entry_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
